import React from 'react';

import { SortableTable } from '~/components/core';
import EmptyState from '~/components/core/EmptyState';
import { EyeIcon, TrashIcon } from '~/components/icons';
import PencilIcon from '~/components/icons/PencilIcon';
import InlineIconButtonUntyped from '~/components/InlineIconButton';
import Switch from '~/components/ui/atoms/Switch';

import TEXTS from './texts';
import type { ExternalToolConfig } from './types';

// InlineIconButton is a .jsx file; under allowJs its optional props (including `ariaLabel`) are
// inferred as the literal `undefined` type. Cast to the runtime shape so a real label can be passed.
interface InlineIconButtonProps {
  icon: React.ElementType;
  tooltipTitle?: string;
  ariaLabel?: string;
  onClick?: () => void;
  disabled?: boolean;
}
const InlineIconButton = InlineIconButtonUntyped as React.ComponentType<InlineIconButtonProps>;

interface ExternalToolConfigsTableProps {
  configs: ExternalToolConfig[];
  onEdit: (config: ExternalToolConfig) => void;
  onDelete: (config: ExternalToolConfig) => void;
  onPreviewSchema: (config: ExternalToolConfig) => void;
  onToggleEnabled: (config: ExternalToolConfig, isEnabled: boolean) => void;
  // tool_names with an in-flight write PUT (row toggle or edit-dialog save); their switch is
  // disabled until it settles so a toggle can't race the in-flight write.
  writingTools: Set<string>;
  // tool_names whose available-tool entry carries a response schema; the preview button is disabled
  // for rows without one (a deregistered tool, or a schema that failed to resolve to null).
  toolsWithSchema: Set<string>;
}

const ExternalToolConfigsTable: React.FC<ExternalToolConfigsTableProps> = ({
  configs,
  onEdit,
  onDelete,
  onPreviewSchema,
  onToggleEnabled,
  writingTools,
  toolsWithSchema,
}) => {
  const columns = [
    { id: 'tool_title', label: TEXTS.columns.tool, numeric: false },
    { id: 'url', label: TEXTS.columns.url, numeric: false },
    {
      id: 'is_enabled',
      label: TEXTS.columns.enabled,
      disableSort: true,
      specialCell: (row: ExternalToolConfig) => (
        <Switch
          checked={row.is_enabled}
          disabled={writingTools.has(row.tool_name)}
          aria-label={`${TEXTS.columns.enabled} ${row.tool_title}`}
          onChange={(_event, checked) => onToggleEnabled(row, checked)}
        />
      ),
    },
    {
      id: 'schema',
      label: TEXTS.columns.schema,
      disableSort: true,
      specialCell: (row: ExternalToolConfig) => {
        const hasSchema = toolsWithSchema.has(row.tool_name);
        return (
          <InlineIconButton
            icon={EyeIcon}
            tooltipTitle={hasSchema ? TEXTS.responseSchema.previewTooltip : TEXTS.responseSchema.noSchemaTooltip}
            ariaLabel={`View response schema ${row.tool_title}`}
            onClick={() => onPreviewSchema(row)}
            disabled={!hasSchema}
          />
        );
      },
    },
    {
      id: 'edit',
      label: TEXTS.columns.edit,
      disableSort: true,
      specialCell: (row: ExternalToolConfig) => (
        <InlineIconButton
          icon={PencilIcon}
          tooltipTitle={TEXTS.columns.edit}
          ariaLabel={`Edit ${row.tool_title}`}
          onClick={() => onEdit(row)}
        />
      ),
    },
    {
      id: 'delete',
      label: TEXTS.columns.delete,
      disableSort: true,
      specialCell: (row: ExternalToolConfig) => (
        <InlineIconButton
          icon={TrashIcon}
          tooltipTitle={TEXTS.columns.delete}
          ariaLabel={`Delete ${row.tool_title}`}
          onClick={() => onDelete(row)}
        />
      ),
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={configs}
      emptyStateComponent={
        <EmptyState title="No external tools" subtitle="No external tool APIs are configured yet." />
      }
    />
  );
};

export default ExternalToolConfigsTable;
